import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable, SearchInput, Button, ErrorState, useDataTableUrl, useToastActions } from '@airclou/ui'
import type { DataTableColumn } from '@airclou/ui'
import { Download, ExternalLink } from 'lucide-react'
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'
import { listInvoices, openBillingPortal } from '../../../services/billing'
import type { InvoiceSummary } from '../../../services/billing'
import { InvoiceStatusBadge, formatMoney } from '../components/invoiceDisplay'
import { usePageRestored } from '../../../hooks/usePageRestored'

/**
 * The organization's invoices, read live from Stripe.
 *
 * There is no local copy to fall back on and none is wanted: an invoice is
 * Stripe's record, and the PDF and the pay page are Stripe's too. An
 * organization that has not subscribed has no invoices, and the empty state
 * says so rather than filling the table.
 */
export function InvoicesTab() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard')
  const toast = useToastActions()
  // Search and sort live in the URL (same pattern as the audit log), so they
  // survive a reload, a shared link and back/forward.
  const { search: searchQuery, onSearchChange, sorting, onSortingChange } = useDataTableUrl()

  const [invoices, setInvoices] = useState<InvoiceSummary[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setIsLoading(true)
    setError(null)
    try {
      const view = await listInvoices()
      setInvoices(view.invoices)
      setHasMore(view.hasMore)
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t('billing.invoicesLoadFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  // Older invoices, past the page Stripe returns, are in the portal. On
  // success the browser leaves for Stripe and comes back to this tab.
  const [openingPortal, setOpeningPortal] = useState(false)
  async function openPortal() {
    setOpeningPortal(true)
    try {
      await openBillingPortal({ returnTab: 'invoices' })
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : t('billing.portalFailed'))
      setOpeningPortal(false)
    }
  }

  // Back from the portal instead of its return link: re-arm the button and
  // re-read, since the portal may have paid an invoice.
  usePageRestored(() => {
    setOpeningPortal(false)
    void load()
  })

  const filteredInvoices = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return invoices
    return invoices.filter(
      (inv) =>
        (inv.number ?? '').toLowerCase().includes(q) ||
        (inv.description ?? '').toLowerCase().includes(q)
    )
  }, [invoices, searchQuery])

  const formatDate = (iso: string) => formatDateInTz(iso, timezone)

  const columns: DataTableColumn<InvoiceSummary>[] = [
    // Every column is sortable by default and sorts by its accessor, so each
    // data column names one; the actions column opts out.
    {
      id: 'number',
      header: t('billing.invoice'),
      accessorKey: 'number',
      cell: (invoice) => <span className="font-medium">{invoice.number ?? invoice.id}</span>,
    },
    {
      id: 'createdAt',
      header: t('billing.date'),
      accessorKey: 'createdAt',
      cell: (invoice) => <span className="text-sm text-muted-foreground">{formatDate(invoice.createdAt)}</span>,
    },
    {
      id: 'description',
      header: t('billing.description'),
      accessorKey: 'description',
      cell: (invoice) => <span className="text-sm">{invoice.description ?? '—'}</span>,
    },
    {
      id: 'amount',
      header: t('billing.amount'),
      accessorKey: 'totalCents',
      cell: (invoice) => <span className="font-medium">{formatMoney(invoice.totalCents, invoice.currency)}</span>,
    },
    {
      id: 'status',
      header: t('billing.status'),
      accessorKey: 'status',
      cell: (invoice) => <InvoiceStatusBadge status={invoice.status} />,
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: (invoice) => (
        <span className="flex items-center justify-end gap-3">
          {/* An open invoice is one Stripe is still collecting: the hosted page is where it is paid. */}
          {invoice.status === 'open' && invoice.hostedInvoiceUrl && (
            <a
              href={invoice.hostedInvoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              {t('billing.payNow')}
            </a>
          )}
          {invoice.pdfUrl && (
            <a
              href={invoice.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              <Download className="w-4 h-4" />
              {t('billing.downloadPdf')}
            </a>
          )}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-sm">
          <SearchInput
            value={searchQuery}
            onChange={onSearchChange}
            placeholder={t('billing.searchInvoices')}
          />
        </div>
      </div>
      {error ? (
        <ErrorState
          title={t('billing.invoicesLoadFailed')}
          description={error}
          action={
            <Button variant="outline" size="sm" onClick={() => void load()}>
              {t('common.retry')}
            </Button>
          }
        />
      ) : (
        <>
          <DataTable
            data={filteredInvoices}
            columns={columns}
            sorting={sorting}
            onSortingChange={onSortingChange}
            isLoading={isLoading && invoices.length === 0}
            emptyMessage={searchQuery ? t('billing.noInvoicesFound') : t('billing.noInvoicesYet')}
          />
          {hasMore && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span>{t('billing.moreInvoicesInPortal', { count: invoices.length })}</span>
              <button
                type="button"
                disabled={openingPortal}
                onClick={() => void openPortal()}
                className="inline-flex items-center gap-1 font-medium text-primary hover:underline disabled:opacity-50"
              >
                <ExternalLink className="w-4 h-4" />
                {openingPortal ? t('billing.openingPortal') : t('billing.manageInPortal')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
